import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { deleteClientCharacteristic } from "actions/clientCharacteristics";
import type { ClientCharacteristic } from "types/domain";

interface DeleteConfirmationModalProps {
  open: boolean;
  characteristic: ClientCharacteristic;
  onClose: () => void;
  /** Optional callback fired after successful deletion for optimistic list removal */
  onDeleteComplete?: (id: number) => void;
}

/**
 * Confirmation modal shown before deleting a characteristic.
 * Displays the characteristic name and warns about cascading deletion of values.
 */
export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  open,
  characteristic,
  onClose,
  onDeleteComplete,
}) => {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await deleteClientCharacteristic(characteristic.id);
      toast.success("Characteristic deleted");
      onDeleteComplete?.(characteristic.id);
      onClose();
    } catch {
      toast.error("Delete failed. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    if (!deleting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      data-testid="delete-confirmation-modal"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Delete {characteristic.name}?
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          This will remove the field and all saved values for this characteristic
          across all clients. This cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="cancel-delete-button"
          onClick={handleCancel}
          disabled={deleting}
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          data-testid="confirm-delete-button"
          onClick={handleConfirm}
          disabled={deleting}
          color="error"
          variant="contained"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationModal;
